import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page" className="pb-32">
      <section className="container-luxe pt-16 md:pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="overline">About the Practice</span>
          <h1 className="mt-5 font-[Cormorant_Garamond] text-5xl text-[#1A1D1A] md:text-6xl">
            A calmer way to own a serviced apartment.
          </h1>
          <div className="gold-divider mt-6" />
          <p className="mt-7 text-base leading-relaxed text-[#4A524C] md:text-lg">
            Sagita Rasmadi LLC is a boutique property-management and
            hospitality consultancy. We manage premium serviced apartments
            across the United Kingdom on behalf of private owners, family
            offices, and investors — under bespoke, written mandates.
          </p>
        </motion.div>
      </section>

      <section className="container-luxe mt-20 grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <div className="overflow-hidden rounded-3xl shadow-[0_20px_50px_rgba(26,29,26,0.10)]">
            <div className="aspect-[4/5] bg-[#D9CFC2]">
              <img
                src="/apartments/generated/the-langford-01.jpg"
                alt="A residence managed by Sagita Rasmadi"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>

        <div className="space-y-6 text-base leading-relaxed text-[#4A524C] lg:col-span-6 lg:text-lg">
          <span className="overline">Our Practice</span>
          <h2 className="font-[Cormorant_Garamond] text-3xl text-[#1A1D1A] md:text-4xl">
            Owner-aligned. Editorial. Quiet.
          </h2>
          <div className="h-px w-12 bg-[#C6A66B]" />
          <p>
            We are not a lettings platform, a booking site, or a short-let
            aggregator. We are a small consulting practice with a deliberately
            limited roster of client-owned residences under our care.
          </p>
          <p>
            Every mandate begins with a confidential conversation and results
            in a written brief covering scope, styling direction, operating
            standard and fee schedule. Our success is measured in your yield
            and your peace of mind — not in transaction volume.
          </p>
          <p>
            We operate primarily across London, with select mandates in the
            wider UK and continental Europe. Owner retention across the last
            five years sits at 100%.
          </p>
        </div>
      </section>

      <section className="container-luxe mt-24 grid gap-8 md:grid-cols-3">
        {[
          {
            t: "Owner-aligned mandates",
            d: "Written scope, transparent fees, no hidden margins. Our incentives are your incentives.",
          },
          {
            t: "Design-led yield strategy",
            d: "We only recommend what we would do to our own portfolio. Every intervention is measured.",
          },
          {
            t: "Hospitality, not lettings",
            d: "Concierge-grade service, editorial photography, and calm, considered guest care.",
          },
        ].map((v) => (
          <div
            key={v.t}
            className="rounded-2xl border border-[#D9CFC2]/60 bg-white p-8 shadow-[0_8px_30px_rgba(26,29,26,0.04)]"
          >
            <h3 className="font-[Cormorant_Garamond] text-2xl text-[#1A1D1A]">
              {v.t}
            </h3>
            <div className="mt-3 h-px w-10 bg-[#C6A66B]" />
            <p className="mt-4 text-sm leading-relaxed text-[#4A524C]">
              {v.d}
            </p>
          </div>
        ))}
      </section>

      <section className="container-luxe mt-24 text-center">
        <h3 className="font-[Cormorant_Garamond] text-3xl text-[#1A1D1A] md:text-4xl">
          Curious how the practice could apply to your property?
        </h3>
        <Link
          to="/contact"
          data-testid="about-cta-contact"
          className="btn-primary mt-8"
        >
          Request Consultation
          <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
}
